/**
 * openingBook.data.ts
 *
 * Canonical ECO → opening data lookup used by OpeningsTheory and OpeningTraining.
 *
 * Each entry contains:
 *  - name        Display name
 *  - moves       Main line in UCI notation (2 chars from + 2 chars to, optionally promotion char)
 *  - description Brief text about the opening
 *  - planText    Key strategic ideas, shown per move group
 *  - side        'w' or 'b' (who initiates the opening)
 */
const OPENING_DATA = [
    // ── E2 OPENINGS (1.e4 lines) ──────────────────────────────────────────────
    {
        eco: 'C50',
        name: 'Apertura Italiana',
        side: 'w',
        moves: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1c4', 'f8c5', 'c2c3', 'g8f6', 'd2d4', 'e5d4', 'c3d4', 'c5b4', 'b1c3', 'g8f6'],
        description: 'La Italiana (Giuoco Piano) es una de las aperturas más antiguas. Blancas atacan el centro con d4 después de desarrollar alfil a c4.',
        explanations: [
            '1.e4 e5: Centro abierto. Ambos bandos luchan por el control del centro.',
            '2.Cf3 Cc6: El caballo ataca el peón e5 y blancas desarrollan con tempo.',
            '3.Ac4: Apunta al punto débil f7. Fórmula clásica del Giuoco Piano.',
            '4...Ac5: Las negras responden simétricamente. Lucha por el control del centro.',
            '5.c3 Cf6: Preparando d4. El caballo defiende el peón e5.',
            '6.d4 exd4 7.cxd4: Blancas abren la posición. El alfil b4 pone presión.',
            '7...Cc6: Las negras activan el caballo hacia el centro.',
        ],
    },
    {
        eco: 'C60',
        name: 'Apertura Española (Ruy López)',
        side: 'w',
        moves: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5', 'a7a6', 'f1a4', 'g8f6', 'e1g1', 'f8e7', 'f1e1', 'b7b5', 'a4b3', 'd7d6'],
        description: 'La Española es la apertura más jugada a alto nivel. Blancas presionan el defensor del peón e5 para ganar ventaja posicional a largo plazo.',
        explanations: [
            '1.e4 e5 2.Cf3 Cc6: Apertura abierta clásica.',
            '3.Ab5: El Ruy López. El alfil pone presión indirecta al peón e5.',
            '3...a6 4.Aa4: Defensa Morphy. Las negras ceden al alfil tempo para ganar espacio.',
            '4...Cf6 5.O-O: Blancas enrocan antes de emprender el ataque.',
            '5...Ae7 6.Te1: Blancas refuerzan el peón e4 y preparan el ataque central.',
            '6...b5 7.Ab3: El alfil se retira pero sigue activo en la diagonal.',
            '7...d6: Defensa Cerrada. Las negras construyen una "tortuga" sólida.',
        ],
    },
    {
        eco: 'C30',
        name: 'Gambito de Rey',
        side: 'w',
        moves: ['e2e4', 'e7e5', 'f2f4', 'e5f4', 'g1f3', 'g7g5', 'h2h4', 'g5g4', 'f3e5', 'd7d5'],
        description: 'El Gambito de Rey es una apertura táctica explosiva. Blancas sacrifican un peón para tener iniciativa y ataque rápido.',
        explanations: [
            '1.e4 e5 2.f4: Gambito de Rey. Blancas sacrifican para abrir la columna f.',
            '2...exf4: Aceptando el gambito. Las negras toman el peón pero ceden el centro.',
            '3.Cf3 g5: Defensa del peón gambiteado. La posición se vuelve muy aguda.',
            '4.h4 g4 5.Ce5: El caballo salta al centro con fuerza. Posición muy táctica.',
            '5...d5: Las negras contraatacan en el centro para liberar su posición.',
        ],
    },
    {
        eco: 'C40',
        name: 'Defensa Rusa (Petroff)',
        side: 'b',
        moves: ['e2e4', 'e7e5', 'g1f3', 'g8f6', 'f3e5', 'd7d6', 'e5f3', 'f6e4', 'd2d4', 'd6d5', 'f1d3', 'e4d6'],
        description: 'La Defensa Petroff es extremadamente sólida. Las negras atacan el peón e4 en lugar de defender el e5, buscando igualdad.',
        explanations: [
            '1.e4 e5 2.Cf3 Cf6: Contrataque en lugar de defensa pasiva.',
            '3.Cxe5 d6 4.Cf3: Las blancas se retiran, las negras reestablecen el material.',
            '4...Cxe4 5.d4: Blancas ganan el centro mientras negras toman el peón.',
            '5...d5 6.Ad3 Cd6: Posición simétrica y sólida para las negras.',
        ],
    },
    {
        eco: 'B20',
        name: 'Defensa Siciliana',
        side: 'b',
        moves: ['e2e4', 'c7c5', 'g1f3', 'd7d6', 'd2d4', 'c5d4', 'f3d4', 'g8f6', 'b1c3', 'a7a6', 'c1g5', 'e7e6', 'f2f4', 'd8b6'],
        description: 'La Siciliana es la defensa más popular. Las negras evitan la simetría y crean juego asimétrico con posibilidades de contraataque.',
        explanations: [
            '1.e4 c5: La Siciliana. Las negras controlan d4 desde el flanco.',
            '2.Cf3 d6 3.d4: Apertura Siciliana Abierta. Blancas abren el centro.',
            '3...cxd4 4.Cxd4 Cf6 5.Cc3: Desarrollo clásico. Las negras se preparan para el contrajuego.',
            '5...a6 6.Ag5: Variante Inglesa. Blancas aumentan la presión en el centro.',
            '6...e6 7.f4: Ataque con f4. Preparando f5 con ataque en el flanco del rey.',
            '7...Db6: Las negras atacan el peón d4 para complicar la posición.',
        ],
    },
    {
        eco: 'B70',
        name: 'Siciliana — Dragón',
        side: 'b',
        moves: ['e2e4', 'c7c5', 'g1f3', 'd7d6', 'd2d4', 'c5d4', 'f3d4', 'g8f6', 'b1c3', 'g7g6', 'c1e3', 'f8g7', 'f2f3', 'e8g8', 'd1d2', 'b8c6'],
        description: 'El Dragón es la variante más afilada de la Siciliana. Las negras desarrollan el alfil en g7 apuntando a la diagonal larga.',
        explanations: [
            '1.e4 c5 2.Cf3 d6 3.d4 cxd4 4.Cxd4 Cf6 5.Cc3 g6: El Dragón. Formación fianchetto.',
            '6.Ae3 Ag7: El alfil "dragón" en g7 controla toda la diagonal.',
            '7.f3 O-O 8.Dd2: Ataque Yugoslavo. Muy agudo. Blancas atacan el rey.',
            '8...Cc6: Las negras se preparan para el contragolpe con ...d5.',
        ],
    },
    {
        eco: 'C00',
        name: 'Defensa Francesa',
        side: 'b',
        moves: ['e2e4', 'e7e6', 'd2d4', 'd7d5', 'b1c3', 'g8f6', 'c1g5', 'f8b4', 'e4e5', 'h7h6', 'g5d2', 'b8c6', 'a2a3', 'b4c3', 'd2c3'],
        description: 'La Francesa es sólida y combativa. Las negras crean un bloqueo en el centro y buscan el contrajuego en el flanco de dama.',
        explanations: [
            '1.e4 e6 2.d4: La Francesa. Las negras preparan d5.',
            '2...d5 3.Cc3: Variante Winawer o Clásica. Lucha por el centro.',
            '3...Cf6 4.Ag5 Ab4: Presión mutua. Las negras clavetan el caballo.',
            '4...h6 5.Axf6: Blancas intercambian el alfil por el caballo.',
            '5...Cc6: Las negras activan el caballo con tempo.',
            '6.a3: Forzando la decisión del alfil en b4.',
        ],
    },
    {
        eco: 'B10',
        name: 'Defensa Caro-Kann',
        side: 'b',
        moves: ['e2e4', 'c7c6', 'd2d4', 'd7d5', 'b1c3', 'd5e4', 'c3e4', 'c8f5', 'e4g3', 'f5g6', 'h2h4', 'h7h6', 'g1f3', 'b8d7', 'h4h5', 'g6h7'],
        description: 'La Caro-Kann es extremadamente sólida. Las negras preparan d5 con c6 para evitar los problemas del alfil dama de la Francesa.',
        explanations: [
            '1.e4 c6 2.d4 d5: La Caro-Kann. Preparando d5 con apoyo de c6.',
            '3.Cc3 dxe4 4.Cxe4: Las negras intercambian en el centro y consiguen estructura sana.',
            '4...Af5 5.Cg3: El alfil se activa antes de cerrar la posición.',
            '5...Ag6 6.h4: Ataque clásico de blancas contra el alfil.',
            '6...h6 7.Cf3 Cd7: Desarrollo sólido.',
            '7...h5 8.Ah7: El alfil va a una casilla pasiva pero forzada.',
        ],
    },
    {
        eco: 'D00',
        name: 'Gambito de Dama Rehusado',
        side: 'w',
        moves: ['d2d4', 'd7d5', 'c2c4', 'e7e6', 'b1c3', 'g8f6', 'c1g5', 'f8e7', 'e2e3', 'e8g8', 'g1f3', 'b8d7', 'f1d3', 'd5c4', 'd3c4'],
        description: 'El GDR es una de las aperturas más clásicas. Las negras rechazan el gambito y construyen una posición sólida en el centro.',
        explanations: [
            '1.d4 d5 2.c4: Gambito de Dama. Blancas ofrecen el peón c4.',
            '2...e6 3.Cc3: Rehusado. Las negras protegen d5 con e6.',
            '3...Cf6 4.Ag5 Ae7: Variante Ortodoxa. Las negras se desarrollan sólidamente.',
            '4...O-O 5.e3: Preparando el desarrollo del alfil en f1.',
            '5...Cd7 6.Cf3: Ambos bandos completan el desarrollo.',
            '6...dxc4 7.Axc4: Las negras aceptan tardíamente el gambito.',
        ],
    },
    {
        eco: 'D10',
        name: 'Defensa Eslava',
        side: 'b',
        moves: ['d2d4', 'd7d5', 'c2c4', 'c7c6', 'g1f3', 'g8f6', 'b1c3', 'd5c4', 'a2a4', 'c8f5', 'e2e3', 'e7e6', 'f1c4', 'f8b4'],
        description: 'La Eslava permite d5 sin encerrar el alfil de dama. Es más flexible que el GDR clásico y muy popular en torneos.',
        explanations: [
            '1.d4 d5 2.c4 c6: La Eslava. c6 apoya d5 sin encerrar el alfil c8.',
            '3.Cf3 Cf6 4.Cc3: Desarrollo clásico para ambos bandos.',
            '4...dxc4 5.a4: Blancas evitan b5. Las negras tomaron el gambito.',
            '5...Af5: El alfil se activa antes de jugar e6.',
            '6.e3 e6 7.Axc4: Blancas recuperan el peón con buena posición.',
        ],
    },
    {
        eco: 'E60',
        name: 'India de Rey (KID)',
        side: 'b',
        moves: ['d2d4', 'g8f6', 'c2c4', 'g7g6', 'b1c3', 'f8g7', 'e2e4', 'd7d6', 'g1f3', 'e8g8', 'f1e2', 'e7e5', 'e1g1', 'b8c6', 'd4d5'],
        description: 'La India de Rey es una defensa hipermoderna. Las negras ceden el centro para luego atacarlo con e5 y c5.',
        explanations: [
            '1.d4 Cf6 2.c4 g6: La India de Rey. Preparando el fianchetto.',
            '3.Cc3 Ag7 4.e4: Blancas construyen un gran centro de peones.',
            '4...d6 5.Cf3 O-O: Las negras enrocan rápidamente.',
            '5...Ae2 e5: Contragolpe clásico del KID.',
            '6.O-O Cc6 7.d5: Blancas cierran el centro. Posición estratégica compleja.',
        ],
    },
    {
        eco: 'E20',
        name: 'Nimzo-Indiana',
        side: 'b',
        moves: ['d2d4', 'g8f6', 'c2c4', 'e7e6', 'b1c3', 'f8b4', 'd1c2', 'e8g8', 'a2a3', 'b4c3', 'c2c3', 'b7b6', 'c1g5', 'c8b7'],
        description: 'La Nimzo-India es una de las defensas más sólidas. Las negras clavan el caballo c3 con el alfil b4 para desorganizar el centro.',
        explanations: [
            '1.d4 Cf6 2.c4 e6 3.Cc3 Ab4: El alfil clava el caballo c3.',
            '4.Dc2: Variante Clásica. Blancas preparan recuperar en c3.',
            '4...O-O 5.a3 Axc3+ 6.Dxc3: Las negras doblaron los peones de blancas.',
            '6...b6 7.Ag5 Ab7: Las negras desarrollan el alfil en la diagonal larga.',
        ],
    },
    {
        eco: 'E80',
        name: 'Grünfeld',
        side: 'b',
        moves: ['d2d4', 'g8f6', 'c2c4', 'g7g6', 'b1c3', 'd7d5', 'c4d5', 'f6d5', 'e2e4', 'd5c3', 'b2c3', 'f8g7', 'f1c4', 'c7c5'],
        description: 'La Grünfeld es una defensa hipermoderna. Las negras dejan que blancas construyan un centro poderoso para luego atacarlo.',
        explanations: [
            '1.d4 Cf6 2.c4 g6 3.Cc3 d5: La Grünfeld. Desafía el centro directamente.',
            '4.cxd5 Cxd5 5.e4: Blancas construyen un centro masivo.',
            '5...Cxc3 6.bxc3: Las negras destruyen el centro a cambio de doblar peones blancos.',
            '6...Ag7 7.Ac4 c5: Presión sobre el centro blanco con el alfil y c5.',
        ],
    },
    {
        eco: 'A10',
        name: 'Apertura Inglesa',
        side: 'w',
        moves: ['c2c4', 'e7e5', 'b1c3', 'g8f6', 'g1f3', 'b8c6', 'g2g3', 'f8b4', 'f1g2', 'e8g8', 'e1g1', 'e5e4'],
        description: 'La Inglesa es una apertura flexible. Blancas controlan d5 con c4 e inician un juego posicional con muchas transposiciones.',
        explanations: [
            '1.c4 e5: Inglesa con 1...e5. Las negras responden con el centro.',
            '2.Cc3 Cf6 3.Cf3: Desarrollo clásico. Ambos bandos desarrollan caballos.',
            '3...Cc6 4.g3: Preparando el fianchetto del alfil de rey.',
            '4...Ab4 5.Ag2 O-O: Las negras clavan y enrocan rápidamente.',
            '5...O-O e4: Las negras avanzan el peón con contrajuego activo.',
        ],
    },
    {
        eco: 'A04',
        name: 'Apertura Réti',
        side: 'w',
        moves: ['g1f3', 'd7d5', 'c2c4', 'd5d4', 'b2b4', 'c7c5', 'b4c5', 'g8f6', 'g2g3', 'b8c6', 'f1g2', 'e7e5'],
        description: 'La Réti es una apertura hipermoderna. Blancas controlan el centro desde la distancia con piezas, no con peones.',
        explanations: [
            '1.Cf3 d5 2.c4: La Réti. Gambito de flanco para desestabilizar el centro negro.',
            '2...d4 3.b4: Gambito de peón para gains de tiempos.',
            '3...c5 4.bxc5: El juego se abre. Posición muy táctica.',
            '4...Cf6 5.g3: Preparando el fianchetto.',
            '5...Cc6 6.Ag2 e5: Las negras luchan por el centro.',
        ],
    },
    {
        eco: 'A45',
        name: 'Defensa Trompowsky',
        side: 'w',
        moves: ['d2d4', 'g8f6', 'c1g5', 'e7e6', 'e2e4', 'h7h6', 'g5f6', 'd8f6', 'b1c3', 'f8b4', 'g1f3', 'b8c6'],
        description: 'El Trompowsky es un ataque sorpresivo. Blancas juegan Ag5 inusualmente temprano para provocar debilidades en la posición negra.',
        explanations: [
            '1.d4 Cf6 2.Ag5: Trompowsky. Jugada de sorpresa para salir del libro.',
            '2...e6 3.e4: Blancas ocupan el centro con fuerza.',
            '3...h6 4.Axf6 Dxf6: Las negras toman con la dama para ganar actividad.',
            '4...Cc3 Ab4: Clavada del caballo en c3.',
            '5.Cf3 Cc6: Ambos completan el desarrollo.',
        ],
    },
    {
        eco: 'C70',
        name: 'Española — Línea Berlín',
        side: 'b',
        moves: ['e2e4', 'e7e5', 'g1f3', 'b8c6', 'f1b5', 'g8f6', 'e1g1', 'f6e4', 'd2d4', 'e4d6', 'b5c6', 'd7c6', 'd4e5', 'd6f5'],
        description: 'La Línea Berlín es una defensa muy sólida, llevada a la fama por Kramnik. Las negras aceptan una estructura de peones asimétrica a cambio de solidez.',
        explanations: [
            '1.e4 e5 2.Cf3 Cc6 3.Ab5 Cf6: El Berlín. Contrataque directo.',
            '4.O-O Cxe4: Las negras toman el peón e4 con valentía.',
            '5.d4 Cd6 6.Axc6: Blancas doblam peones para abrir el juego.',
            '6...dxc6 7.dxe5 Cf5: Los finales de Berlín son muy técnicos.',
        ],
    },
    {
        eco: 'D30',
        name: 'Gambito de Dama Aceptado',
        side: 'b',
        moves: ['d2d4', 'd7d5', 'c2c4', 'd5c4', 'g1f3', 'g8f6', 'e2e3', 'e7e6', 'f1c4', 'c7c5', 'e1g1', 'a7a6', 'd1e2', 'b7b5', 'c4b3'],
        description: 'En el GDA las negras aceptan el gambito. Buscan igualdad en el desarrollo a cambio de ceder temporalmente el centro.',
        explanations: [
            '1.d4 d5 2.c4 dxc4: Las negras aceptan el gambito.',
            '3.Cf3 Cf6 4.e3: Preparando recuperar el peón con Axc4.',
            '4...e6 5.Axc4 c5: Las negras contraatacan el centro con c5.',
            '5...O-O a6: Preparando b5 para expulsar el alfil.',
            '6.De2 b5 7.Ab3: El alfil se retira a una casilla segura.',
        ],
    },
];
/** Quick lookup by ECO code */
const OPENING_MAP = new Map(OPENING_DATA.map(d => [d.eco, d]));
/**
 * Returns opening data for the given ECO code, or null if not found.
 */
export function getOpeningData(eco) {
    return OPENING_MAP.get(eco) ?? null;
}
/**
 * Returns all openings available in the embedded book.
 */
export function getAllOpeningData() {
    return OPENING_DATA;
}
export default OPENING_DATA;
